import { inject } from '@angular/core';
import {
    HttpInterceptorFn,
    HttpRequest,
    HttpHandlerFn,
    HttpErrorResponse
} from '@angular/common/http';
import { BehaviorSubject, catchError, delay, filter, of, switchMap, take, throwError } from 'rxjs';
import { SessionService } from '@core/services/session.service';
import { AuthService } from '@features/auth/services/auth.service';
import { environment } from '@environments/environment';

// Evita múltiples logouts simultáneos
let isLoggingOut = false;

// Controla que solo haya un refresh en curso a la vez
let isRefreshing = false;
const refreshToken$ = new BehaviorSubject<string | null>(null);

// Errores transitorios por Lambda cold start: reintenta hasta 2 veces con backoff
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1500;

function isTransientError(error: HttpErrorResponse): boolean {
    // status 0 = CORS/red (preflight falló), 503 = Lambda no disponible aún
    return error.status === 0 || error.status === 503;
}

function buildHeaders(token: string | null, deviceId: string): Record<string, string> {
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (deviceId) headers['x-device-id'] = deviceId;
    return headers;
}

export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
    // Solo agrega headers de auth a peticiones hacia nuestras propias APIs
    if (!req.url.startsWith(environment.API_URL) && !req.url.startsWith(environment.INSIGHTS_URL)) {
        return next(req);
    }

    const session = inject(SessionService);
    const auth = inject(AuthService);
    const token = session.getToken();
    const deviceId = session.getDeviceId();

    const headers = buildHeaders(token, deviceId);
    const authReq = Object.keys(headers).length === 0 ? req : req.clone({ setHeaders: headers });

    // El core API es booking-back. El insights service es un microservicio separado
    // y puede retornar 401 por razones no relacionadas a la sesión (ej: x-anonymous-id faltante).
    // Solo el core API debe disparar logout en 401.
    const isCoreApi = req.url.startsWith(environment.API_URL) && !req.url.startsWith(environment.INSIGHTS_URL);

    let retries = 0;

    const execute = (): ReturnType<HttpHandlerFn> => next(authReq).pipe(
        catchError((error: HttpErrorResponse) => {
            // Reintentar errores transitorios (Lambda cold start)
            if (isTransientError(error) && retries < MAX_RETRIES) {
                retries++;
                return of(null).pipe(
                    delay(RETRY_DELAY_MS * retries),
                    switchMap(() => execute())
                );
            }

            const isAuthRoute = req.url.includes('/auth/signout')
                || req.url.includes('/auth/refresh')
                || req.url.includes('/auth/signin');

            if (error.status === 401 && !isAuthRoute) {
                // Si el backend confirmó que el JWT expiró → intentar refresh (aplica a ambas APIs)
                if (error.error?.error === 'TOKEN_EXPIRED') {
                    return handleRefresh(req, next, session, auth);
                }

                // Solo el core API dispara logout — el insights service puede retornar 401
                // por causas no relacionadas a la sesión (missing x-anonymous-id, etc.)
                if (isCoreApi && !isLoggingOut) {
                    isLoggingOut = true;
                    auth.signOut().subscribe({ error: () => {} });
                    session.logout();
                    setTimeout(() => { isLoggingOut = false; }, 2000);
                }
                return throwError(() => error);
            }

            return throwError(() => error);
        })
    );

    return execute();
};

/**
 * Intenta renovar el JWT usando el PAT como refresh token.
 * Si hay un refresh en curso, encola la petición y la reintenta con el nuevo token.
 * Si el refresh falla, cierra sesión.
 */
function handleRefresh(
    req: HttpRequest<unknown>,
    next: HttpHandlerFn,
    session: SessionService,
    auth: AuthService
): ReturnType<HttpHandlerFn> {
    if (!isRefreshing) {
        isRefreshing = true;
        refreshToken$.next(null);

        return auth.refresh().pipe(
            switchMap(res => {
                isRefreshing = false;
                const newToken = res.data?.token;
                if (!newToken) throw new Error('Refresh: respuesta sin token');
                session.setToken(newToken);
                refreshToken$.next(newToken);
                // Reintentar la petición original con el nuevo token
                const deviceId = session.getDeviceId();
                return next(req.clone({ setHeaders: buildHeaders(newToken, deviceId) }));
            }),
            catchError(refreshError => {
                isRefreshing = false;
                refreshToken$.next(null);
                // El refresh falló — el PAT venció o fue revocado → logout
                if (!isLoggingOut) {
                    isLoggingOut = true;
                    auth.signOut().subscribe({ error: () => {} });
                    session.logout();
                    setTimeout(() => { isLoggingOut = false; }, 2000);
                }
                return throwError(() => refreshError);
            })
        );
    }

    // Ya hay un refresh en curso: esperar el nuevo token y reintentar
    return refreshToken$.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(newToken => {
            const deviceId = session.getDeviceId();
            return next(req.clone({ setHeaders: buildHeaders(newToken, deviceId) }));
        })
    );
}
